import { apiClient } from './apiClient';
import { toast } from 'sonner';

/**
 * Service for password-related operations
 * Handles password changes, resets, and validation
 */
export const passwordService = {
  /**
   * Change user's password (self-service)
   * This is used when users want to change their own password
   */
  changePassword: async (newPassword: string): Promise<{ success: boolean; error?: string; data?: any }> => {
    try {
      console.log("🔄 Changing password...");
      
      // Validate inputs before sending to Edge Function
      if (!newPassword) {
        return { 
          success: false, 
          error: 'Missing new password' 
        };
      }
      
      if (newPassword.length < 8) {
        return { 
          success: false, 
          error: 'Password must be at least 8 characters long' 
        };
      }
      
      // Use the centralized API client to call the Edge Function
      const { data, error } = await apiClient.invokeEdgeFunction('self-service-password-change', {
        body: {
          newPassword
        }
      });
      
      if (error) {
        console.error("❌ Password change failed:", error);
        
        // Handle specific error cases with better user feedback
        if (error.message?.includes('User profile not found')) {
          return { 
            success: false, 
            error: 'User profile not found. Please contact support.' 
          };
        } else if (error.message?.includes('Password must be at least 8 characters long')) {
          return { 
            success: false, 
            error: 'Password must be at least 8 characters long.' 
          };
        } else if (error.message?.includes('Invalid or expired token')) {
          return { 
            success: false, 
            error: 'Your session has expired. Please log in again.' 
          };
        } else if (error.message?.includes('Server configuration error')) {
          return { 
            success: false, 
            error: 'Server configuration issue. Please contact support.' 
          };
        } else {
          return { 
            success: false, 
            error: `Failed to change password: ${error.message || 'Unknown error'}` 
          };
        }
      }

      if (data?.error) {
        console.error("❌ Password change returned error:", data.error);
        return { 
          success: false, 
          error: `Failed to change password: ${data.error}` 
        };
      }
      
      console.log("✅ Password changed successfully");
      return { 
        success: true, 
        data: data || { message: 'Password changed successfully' } 
      };
      
    } catch (error) {
      console.error("❌ Error changing password:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Password change failed: ${errorMessage}` 
      };
    }
  },

  /**
   * Reset user's password (admin function)
   * This is used by admins to reset other users' passwords
   */
  resetUserPassword: async (userId: string, newPassword: string): Promise<{ success: boolean; error?: string; data?: any }> => {
    try {
      console.log("🔄 Resetting user password...");
      
      // Validate inputs before sending to Edge Function
      if (!userId || !newPassword) {
        return { 
          success: false, 
          error: 'Missing userId or newPassword' 
        };
      }
      
      if (newPassword.length < 8) {
        return { 
          success: false, 
          error: 'Password must be at least 8 characters long' 
        };
      }
      
      // Use the centralized API client to call the Edge Function
      const { data, error } = await apiClient.invokeEdgeFunction('admin-password-reset', {
        body: {
          userId,
          newPassword
        }
      });
      
      if (error) {
        console.error("❌ Password reset failed:", error);
        
        // Handle specific error cases with better user feedback
        if (error.message?.includes('User not found') || error.message?.includes('Target user not found')) {
          return { 
            success: false, 
            error: 'User not found. They may have been deleted.' 
          };
        } else if (error.message?.includes('Password should be') || error.message?.includes('Password must be')) {
          return { 
            success: false, 
            error: 'Password does not meet security requirements. Use at least 8 characters.' 
          };
        } else if (error.message?.includes('insufficient privileges') || error.message?.includes('Insufficient privileges')) {
          return { 
            success: false, 
            error: 'You do not have permission to reset user passwords.' 
          };
        } else if (error.message?.includes('Company mismatch') || error.message?.includes('outside your company')) {
          return { 
            success: false, 
            error: 'You can only reset passwords for users in your company.' 
          };
        } else if (error.message?.includes('Invalid or expired token')) {
          return { 
            success: false, 
            error: 'Your session has expired. Please log in again.' 
          };
        } else if (error.message?.includes('Server configuration error')) {
          return { 
            success: false, 
            error: 'Server configuration issue. Please contact support.' 
          };
        } else if (error.message?.includes('Edge Function returned a non-2xx status code')) {
          return { 
            success: false, 
            error: `Password reset failed: ${error.message}. Check console for details.` 
          };
        } else {
          return { 
            success: false, 
            error: `Failed to reset password: ${error.message || 'Unknown error'}` 
          };
        }
      }

      if (data?.error) {
        console.error("❌ Password reset returned error:", data.error);
        return { 
          success: false, 
          error: `Failed to reset password: ${data.error}` 
        };
      }
      
      console.log("✅ Password reset successfully");
      return { 
        success: true, 
        data: data || { message: 'Password has been reset successfully!' } 
      };
      
    } catch (error) {
      console.error("❌ Error resetting password:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Password reset failed: ${errorMessage}` 
      };
    }
  }
};
